import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  EmployeeAvatar,
  resolvePersonName
} from 'components/common/select/EmployeeBadge';

/**
 * PersonChip — the canonical inline person display (Phase 3), for every
 * "created by / updated by / handled by / sales in-charge / CSE in-charge"
 * style field: the app-wide employee mark (per-name gradient avatar, real
 * photo when available) + short name + full name, kept compact for grid
 * rows. The full record (short name, full name, email, title, site office)
 * lives in the hover/focus tooltip.
 *
 *   <PersonChip name="THV" fullName="Trần Hoàng Vũ" email="thv@x.com"
 *               jobTitle="Sales Executive" site="HCM Office" />
 *   <PersonChip name={user.short_name} size="xs" avatarOnly />  // tooltip only
 */
const SIZES = {
  xs: { avatar: 18, font: 8 },
  sm: { avatar: 22, font: 9 },
  md: { avatar: 28, font: 11 }
};

const PersonChip = ({
  name,
  fullName,
  email,
  jobTitle,
  site,
  avatarUrl,
  size = 'sm',
  stacked = false,
  avatarOnly = false,
  className = ''
}) => {
  if (!name && !fullName && !email) return null;

  const primary = name || fullName || email;
  const resolvedFull = fullName || resolvePersonName({ fullName }, primary);
  const showFull = resolvedFull && resolvedFull !== primary ? resolvedFull : null;
  const sz = SIZES[size] || SIZES.sm;

  // Multi-line native tooltip — robust inside AG Grid cells.
  const tooltip = [
    showFull ? `${primary} — ${showFull}` : primary,
    email,
    jobTitle,
    site
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span
      className={classNames('tw-inline-flex tw-items-center tw-gap-1.5 tw-min-w-0', className)}
      title={tooltip}
      tabIndex={-1}
    >
      <EmployeeAvatar name={primary} avatarUrl={avatarUrl} size={sz.avatar} fontSize={sz.font} />
      {!avatarOnly &&
        (stacked ? (
          <span className="tw-flex tw-flex-col tw-min-w-0 tw-leading-tight">
            <span className="tw-text-[13px] tw-font-medium tw-text-slate-700 tw-truncate">
              {primary}
            </span>
            {showFull && (
              <span className="tw-text-[11px] tw-text-slate-400 tw-truncate">
                {showFull}
              </span>
            )}
          </span>
        ) : (
          <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-slate-700">
            <span className="tw-font-medium">{primary}</span>
            {showFull && (
              <span className="tw-text-slate-400"> · {showFull}</span>
            )}
          </span>
        ))}
    </span>
  );
};

PersonChip.propTypes = {
  name: PropTypes.string,
  fullName: PropTypes.string,
  email: PropTypes.string,
  jobTitle: PropTypes.string,
  site: PropTypes.string,
  avatarUrl: PropTypes.string,
  size: PropTypes.oneOf(Object.keys(SIZES)),
  stacked: PropTypes.bool,
  avatarOnly: PropTypes.bool,
  className: PropTypes.string
};

export default PersonChip;
